import { lazy, Suspense } from 'react'
import { createPortal } from 'react-dom'
import ReactDOM from 'react-dom/client'
import { ErrorBoundary } from 'react-error-boundary'
import { pipe } from 'remeda'
import { CartDrawer } from '@components/CartDrawer/CartDrawer'
import Header from '@components/Header'
import renderLiquidLinks from '@components/Header/utils/renderLiquidLinks'
import withLazyLoad from '@components/LazyLoad/LazyLoad'
import { VisualTopImage } from '@components/VisualTopImage/VisualTopImage'
import type { VisualTopImageProps } from '@components/VisualTopImage/VisualTopImage.types'
import { lazyImport } from '@helpers'
import {
  withAlgoliaSearch,
  withApollo,
  withErrorBoundary,
  withExchangeRates,
  withI18next,
  withSkeletonTheme,
  withStaffPickImage,
  withTheme,
} from '@hocs'
import type { CartDrawerData, CollectionMessageData } from '@velasca/types'
import { ErrorFallback } from '@velasca/ui/error-fallback'
import { $settings } from '@velasca/utils/globals'
import { parseJSONSafe } from '@velasca/utils/parse-json-safe'

const ApparelCollection = lazy(
  () => import('@components/ApparelCollection/ApparelCollection'),
)

const { ApparelCollectionWithFilterResults } = lazyImport(
  () =>
    import(
      '@components/ApparelCollectionWithFilterResults/ApparelCollectionWithFilterResults'
    ),
  'ApparelCollectionWithFilterResults',
)

const { AlgoliaFilters } = lazyImport(
  () => import('@components/AlgoliaSearch/AlgoliaFilters'),
  'AlgoliaFilters',
)

const { RecentProductsSlider } = lazyImport(
  () => import('@components/RecentProductsSlider/RecentProductsSlider'),
  'RecentProductsSlider',
)

const lazyConfig = {
  rootMargin: `800px 0px 800px 0px`,
  triggerOnce: true,
}

const RecentProductsSliderLazyLoad = withLazyLoad<
  React.ComponentProps<typeof RecentProductsSlider>
>((props) => {
  return (
    <Suspense fallback={null}>
      <RecentProductsSlider {...props} />
    </Suspense>
  )
}, lazyConfig)

// TODO: fare la stessa cosa che è stato fatto per le macro: non dividere il provider di Algolia dai suoi hook con suspense
export const renderApparelCollectionApp = (
  apparelCategoryEl: HTMLElement | null,
  visualTopImageData: VisualTopImageProps,
  cartDrawerData: CartDrawerData,
  headerEl: HTMLElement | null,
  headerData: any,
  promotionalBannerData: any,
  collectionTopMessageData: CollectionMessageData,
) => {
  if (apparelCategoryEl) {
    const categoryHandle = apparelCategoryEl.getAttribute(
      'data-collection-handle',
    )
    const rawProductsCount = apparelCategoryEl.getAttribute(
      'data-products-count',
    )
    const productsCount = parseJSONSafe(rawProductsCount || '[]')
    const groupProducts =
      apparelCategoryEl.getAttribute('data-group-products') === 'true'

    if (categoryHandle) {
      const ApparelCollectionApp = () => (
        <>
          <CartDrawer emptyCartCtas={cartDrawerData?.ctas || []} />
          {headerEl &&
            createPortal(
              <Header
                sticky
                promotionalBanner={promotionalBannerData || {}}
                miscHeaderData={headerData?.miscData}
                menu={
                  renderLiquidLinks(
                    headerData?.nav,
                    headerData?.miscData?.liquidLinks,
                  ) ?? undefined
                }
                lowerMenu={
                  headerData?.mobileOnlyNav &&
                  renderLiquidLinks(
                    headerData?.mobileOnlyNav,
                    headerData?.miscData?.liquidLinks,
                  )
                }
              />,
              headerEl,
            )}

          {visualTopImageData && (
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <VisualTopImage
                title={visualTopImageData.title}
                mobileImage={visualTopImageData.mobileImage}
                desktopImage={visualTopImageData.desktopImage}
              />
            </ErrorBoundary>
          )}

          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Suspense fallback={<div className="apparel-collection"></div>}>
              <ErrorBoundary
                fallback={
                  <ApparelCollection
                    showReviewBadge
                    showProductType
                    className="apparel-collection"
                    categoryHandle={categoryHandle}
                    skeletonLength={4}
                    groupProducts={groupProducts}
                    showSingleVariant={groupProducts}
                    productsCount={productsCount}
                    collectionTopMessageData={collectionTopMessageData}
                  />
                }
              >
                {$settings.algolia_enable_filters && <AlgoliaFilters />}
                <ApparelCollectionWithFilterResults
                  showProductType
                  showReviewBadge
                  className="apparel-collection"
                  categoryHandle={categoryHandle}
                  productsCount={productsCount}
                  skeletonLength={4}
                  groupProducts={groupProducts}
                  showSingleVariant={groupProducts}
                  collectionTopMessageData={collectionTopMessageData}
                />
              </ErrorBoundary>
            </Suspense>
          </ErrorBoundary>
          {$settings.recentProductsCollectionEnabled && (
            <RecentProductsSliderLazyLoad />
          )}
        </>
      )

      const ApparelCollectionAppWrapper = pipe(
        ApparelCollectionApp,
        withAlgoliaSearch,
        withStaffPickImage,
        withExchangeRates,
        withSkeletonTheme,
        withI18next,
        withApollo,
        withErrorBoundary,
        withTheme,
      )

      ReactDOM.createRoot(apparelCategoryEl).render(
        <ApparelCollectionAppWrapper />,
      )
    }
  }
}
